import L from 'leaflet'
import Vue from 'vue'
import 'leaflet.markercluster/dist/MarkerCluster.css'
import 'leaflet.markercluster/dist/MarkerCluster.Default.css'
import 'leaflet.markercluster'
import 'leaflet-fullscreen/dist/Leaflet.fullscreen.js'
import 'leaflet-fullscreen/dist/leaflet.fullscreen.css'
import 'leaflet-editable'

// Global Leaflet attribution prefix
if (L.Control?.Attribution) {
  L.Control.Attribution.prototype.options.prefix = 'Moova'
}

class MapService {
  constructor() {
    this.map = null
    this.layers = {}
    this.markers = {}
    this.baseLayers = {} // Store base tile layers
    this.state = Vue.observable({
      tileProviders: [] // Reactive store for provider config
    })
    this.layerOptions = {} // Store options for each layer (type, clustered, etc)
    this.layersVisibility = {} // Track current visibility to avoid stale logs

    this.popupVueInstances = new Map() // Track Vue instances in popups to destroy them
    this.controlVueInstances = [] // Track Vue instances in controls to destroy them

    // Track counts for layers
    this.entityCounts = {}

    // Store and parent for component context
    this.store = null
    this.parent = null
  }

  static getInstance() {
    if (!MapService.instance) {
      MapService.instance = new MapService()
    }
    return MapService.instance
  }

  /**
   * Initialize the map
   * @param {string} elementId - DOM ID of the map container
   * @param {object} options - Leaflet map options
   */
  init(mapElementId, options = {}, store = null, parent = null, layerConfigs = {}) {
    if (this.map) return this.map

    this.store = store
    this.parent = parent

    const { center = [-34.6037, -58.3816], zoom = 13, maxZoom = 18, minZoom = 1, ...leafletOptions } = options

    this.map = L.map(mapElementId, {
      zoomControl: false,
      fullscreenControl: false,
      maxZoom,
      minZoom,
      ...leafletOptions
    }).setView(center, zoom)

    // Set attribution prefix
    if (this.map.attributionControl) {
      this.map.attributionControl.setPrefix('Moova')
    }

    // 1. Fullscreen Control (Top Left)
    if (L.control.fullscreen) {
      L.control.fullscreen({
        position: 'topleft',
        title: {
          false: parent.$gettext('View fullscreen'),
          true: parent.$gettext('Exit fullscreen')
        }
      }).addTo(this.map)
    }

    // 2. Zoom Control (Top Left)
    L.control.zoom({ position: 'topleft' }).addTo(this.map)

    this.initLayers(arguments.length > 4 ? arguments[4] : {})
    return this.map
  }

  getBounds() {
    return this.map ? this.map.getBounds() : null
  }

  getCenter() {
    return this.map ? this.map.getCenter() : null
  }

  getZoom() {
    return this.map ? this.map.getZoom() : 0
  }

  addScaleControl(options = {}) {
    if (!this.map) return

    // Remove existing scale control if any
    if (this.scaleControl) {
      this.map.removeControl(this.scaleControl)
    }

    this.scaleControl = L.control.scale({
      position: 'bottomright',
      metric: true,
      imperial: true,
      ...options
    }).addTo(this.map)

    return this.scaleControl
  }

  setCenter(latlng, zoom) {
    if (this.map) {
      this.map.setView(latlng, zoom || this.map.getZoom())
    }
  }

  setZoom(zoom) {
    if (this.map) {
      this.map.setZoom(zoom)
    }
  }

  fitBounds(bounds, options = {}) {
    if (this.map) {
      this.map.fitBounds(bounds, options)
    }
  }

  fitAll() {
    if (!this.map) return

    const bounds = L.latLngBounds()
    let hasMarkers = false

    Object.keys(this.layers).forEach(type => {
      const layer = this.layers[type]
      if (layer && this.map.hasLayer(layer)) {
        let layerHasContent = false
        const layerBounds = L.latLngBounds()

        // 1. Check our managed markers Map (Items, Movers)
        if (this.markers[type] && this.markers[type].size > 0) {
          this.markers[type].forEach(marker => {
            const latlng = marker.getLatLng()
            if (latlng.lat !== 0 || latlng.lng !== 0) {
              bounds.extend(latlng)
              layerBounds.extend(latlng)
              hasMarkers = true
              layerHasContent = true
            }
          })
        }

        // 2. Check the layer itself for Shippings, Tours, Geofences or extra objects
        // We only trust getBounds() if the layer is not a Cluster or if we actually have layers in it
        if (typeof layer.getBounds === 'function') {
          // For clustered layers, Leaflet's getBounds() often returns stale data when empty.
          // So we verify it actually has layers.
          let actualLeafletLayers = []
          if (typeof layer.getLayers === 'function') {
            actualLeafletLayers = layer.getLayers()
          }

          if (actualLeafletLayers.length > 0) {
            const b = layer.getBounds()
            if (b?.isValid()) {
              // If it was already processed via markers, extending with bounds is redundant but safe
              bounds.extend(b)
              layerBounds.extend(b)
              hasMarkers = true
              layerHasContent = true
            }
          }
        } else if (typeof layer.getLayers === 'function' && !layerHasContent) {
          // 3. Fallback for generic Groups without getBounds
          layer.getLayers().forEach(l => {
            if (l.getLatLng) {
              const latlng = l.getLatLng()
              if (latlng.lat !== 0 || latlng.lng !== 0) {
                bounds.extend(latlng)
                layerBounds.extend(latlng)
                hasMarkers = true
                layerHasContent = true
              }
            }
          })
        }
      }
    })

    if (hasMarkers && bounds.isValid()) {
      this.map.fitBounds(bounds, { padding: [50, 50], maxZoom: 16 })
    }
  }

  setTileProviders(providers) {
    if (!this.map) return
    this.state.tileProviders = providers

    // 1. Remove existing base layers
    Object.keys(this.baseLayers).forEach(name => {
      this.map.removeLayer(this.baseLayers[name])
    })
    this.baseLayers = {}

    // 2. Add new layers
    providers.forEach(p => {
      const layer = L.tileLayer(p.url, {
        attribution: p.attribution,
        maxZoom: p.maxZoom || 18,
        ...p.options
      })
      this.baseLayers[p.name] = layer

      if (p.visible) {
        layer.addTo(this.map)
      }
    })
  }

  switchTileProvider(name) {
    if (!this.map || !this.baseLayers[name]) return

    // Hide all base layers
    Object.keys(this.baseLayers).forEach(n => {
      this.map.removeLayer(this.baseLayers[n])
    })

    // Show the selected one
    this.baseLayers[name].addTo(this.map)

    // Update visibility in our local config copy to stay in sync and trigger reactivity
    this.state.tileProviders = this.state.tileProviders.map(p => ({
      ...p,
      visible: p.name === name
    }))
  }

  get tileProviders() {
    return this.state.tileProviders
  }

  on(event, callback) {
    if (this.map) {
      this.map.on(event, callback)
    }
  }

  invalidateSize() {
    if (this.map) {
      this.map.invalidateSize()
    }
  }

  initLayers(layerConfigs = {}) {
    Object.keys(layerConfigs).forEach(layerKey => {
      const config = layerConfigs[layerKey]
      const params = { clustered: config.clustered, ...(config.layerParams || {}) }

      // Seed initial visibility from config if present
      if (this.layersVisibility[layerKey] === undefined && config.visible !== undefined) {
        this.layersVisibility[layerKey] = config.visible
      }

      // 1. Setup custom pane if configured
      if (params.pane && !this.map.getPane(params.pane)) {
        this.map.createPane(params.pane)
        if (params.zIndex) {
          this.map.getPane(params.pane).style.zIndex = params.zIndex
        }
        if (params.pointerEvents) {
          this.map.getPane(params.pane).style.pointerEvents = params.pointerEvents
        }
      }

      // 2. Initialize layer based on type
      if (params.type === 'geoJSON') {
        // Special case for GeoJSON layers that need custom style functions
        this.layers[layerKey] = L.geoJSON(null, {
          pane: params.pane || 'overlayPane',
          style: (feature) => {
            // Priority: Config function -> Layer Config -> Default
            if (config.style && typeof config.style === 'function') {
              return config.style(feature)
            }
            return {
              fillColor: feature.properties.color || '#3388ff',
              weight: 2,
              opacity: 1,
              color: 'white',
              dashArray: '3',
              fillOpacity: 0.5
            }
          },
          onEachFeature: (feature, layer) => {
            // Basic binding if needed, though usually handled via updateGeofences custom logic
            if (config.markerTooltip) {
              const content = typeof config.markerTooltip === 'function'
                ? config.markerTooltip(feature.properties || feature)
                : config.markerTooltip
              layer.bindTooltip(content, { sticky: true })
            }
          }
        })
        this.layers[layerKey].addTo(this.map)
        this.layerOptions[layerKey] = params
      } else {
        // Standard layers
        this.ensureLayer(layerKey, params)
      }
    })
  }

  ensureLayer(layerType, options = {}) {
    const existing = this.layers[layerType]
    const shouldBeVisible = this.layersVisibility[layerType] !== false

    if (existing) {
      const isActuallyClustered = existing instanceof L.MarkerClusterGroup
      const requestedClustered = !!options.clustered

      if (isActuallyClustered === requestedClustered) {
        // Ensure visibility state is synced with the map
        if (shouldBeVisible && !this.map.hasLayer(existing)) {
          this.map.addLayer(existing)
        } else if (!shouldBeVisible && this.map.hasLayer(existing)) {
          this.map.removeLayer(existing)
        }
        return existing
      }

      // Type mismatch: Remove old layer to recreate it
      this.map.removeLayer(existing)
      delete this.layers[layerType]
      if (this.markers[layerType]) this.markers[layerType].clear()
    }

    if (options.clustered) {
      this.layers[layerType] = L.markerClusterGroup({
        chunkedLoading: true,
        spiderfyOnMaxZoom: true,
        showCoverageOnHover: false,
        zoomToBoundsOnClick: true,
        maxClusterRadius: 50,
        ...options
      })
    } else if (options.type === 'featureGroup') {
      this.layers[layerType] = L.featureGroup([], options)
    } else if (options.type === 'geoJSON') {
      this.layers[layerType] = L.geoJSON(null, options)
    } else {
      this.layers[layerType] = L.layerGroup([], options)
    }

    // Only add to map if it should be visible
    if (shouldBeVisible) {
      this.layers[layerType].addTo(this.map)
    }

    this.markers[layerType] = new Map()
    this.layerOptions[layerType] = options
    return this.layers[layerType]
  }

  /**
   * Smoothly animate a marker to a destination
   * @param {L.Marker} marker
   * @param {L.LatLng} destination
   * @param {number} duration
   */
  slideMarker(marker, destination, duration) {
    if (marker._slideReq) {
      cancelAnimationFrame(marker._slideReq)
    }

    const startLatLng = marker.getLatLng()
    const startTimestamp = performance.now()

    const animate = (timestamp) => {
      const progress = Math.min((timestamp - startTimestamp) / duration, 1)

      const lat = startLatLng.lat + (destination.lat - startLatLng.lat) * progress
      const lng = startLatLng.lng + (destination.lng - startLatLng.lng) * progress

      marker.setLatLng([lat, lng])

      if (progress < 1) {
        marker._slideReq = requestAnimationFrame(animate)
      } else {
        marker._slideReq = null
        marker.setLatLng(destination) // Snap to final
      }
    }

    marker._slideReq = requestAnimationFrame(animate)
  }

  /**
   * Render marker icon from various formats
   * @param {*} renderer - String, Function, or Vue Component
   * @param {Object} data - Data to pass to renderer
   * @param {Object} eventHandlers - Event handlers { eventName: handler }
   * @param {Object} store - Vuex store
   * @param {Vue} parent - Parent Vue instance
   * @returns {Object} { html: string, element: HTMLElement|null, instance: Vue|null }
   */
  renderMarkerIcon(renderer, data, eventHandlers = {}, store = null, parent = null, extraContext = {}) {
    if (!renderer) return { html: '', element: null, instance: null }

    // console.log('renderMarkerIcon called with:', { renderer, data })

    // String HTML
    if (typeof renderer === 'string') {
      return { html: renderer, element: null, instance: null }
    }

    // Vue Component (object with component options or constructor)
    if (typeof renderer === 'object' || typeof renderer === 'function') {
      // It's a Vue component if it's an object with component options
      // or a function with render/template
      const isVueComponent = (typeof renderer === 'object') ||
                            (typeof renderer === 'function' && (renderer.render || renderer.template))

      if (isVueComponent) {
        // console.log('Confirmed Vue component, mounting...')
        const Component = Vue.extend({
          ...renderer,
          store,
          parent
        })
        const instance = new Component({
          propsData: data
        })

        // Attach event listeners
        Object.keys(eventHandlers).forEach(eventName => {
          instance.$on(eventName, eventHandlers[eventName])
        })

        instance.$mount()
        // console.log('Component mounted:', instance)

        // Return both element and instance (don't destroy, keep it alive)
        return {
          html: instance.$el.outerHTML,
          element: instance.$el,
          instance
        }
      }

      // Regular function renderer
      if (typeof renderer === 'function') {
        const result = renderer(data, parent, extraContext)
        if (typeof result === 'object' && result !== null && result.html) {
          return {
            html: result.html,
            iconSize: result.iconSize,
            iconAnchor: result.iconAnchor,
            element: null,
            instance: null
          }
        }
        return { html: result, element: null, instance: null }
      }
    }

    return { html: '', element: null, instance: null }
  }

  /**
   * Generic method to update markers for any layer type
   * @param {string} layerType - Layer key (items, moovers, etc)
   * @param {Array} data - Array of entities to display
   * @param {Object} config - Layer configuration with renderer, tooltip, popup
   */
  updateMarkers(layerType, data, config = {}) {
    if (!this.map) return

    // Ensure layer and marker map exist
    this.ensureLayer(layerType, { clustered: config.clustered })

    const {
      markerRenderer,
      markerTooltip,
      markerPopup,
      animationDuration = 1000,
      removeMissing = true
    } = config
    const validIds = new Set()
    const newMarkers = []

    data.forEach((entity) => {
      validIds.add(entity.id)
      const existing = this.markers[layerType].get(entity.id)

      // Render icon
      let markerRendererResult = null
      let iconHtml = ''
      let vueInstance = null

      if (markerRenderer) {
        markerRendererResult = this.renderMarkerIcon(
          markerRenderer,
          { item: entity }, // Wrap for consistency
          {},
          this.store,
          this.parent,
          { layerType, entity }
        )
        iconHtml = markerRendererResult.html
        vueInstance = markerRendererResult.instance
      } else {
        iconHtml = `<div class="${layerType}-marker">${entity.label || entity.id}</div>`
      }

      const iconOptions = {
        className: `custom-${layerType}-icon`,
        html: iconHtml
      }

      if (markerRendererResult?.iconSize) {
        iconOptions.iconSize = markerRendererResult.iconSize
      } else if (config.iconSize) {
        iconOptions.iconSize = typeof config.iconSize === 'function' ? config.iconSize(entity) : config.iconSize
      }

      if (markerRendererResult?.iconAnchor) {
        iconOptions.iconAnchor = markerRendererResult.iconAnchor
      } else if (config.iconAnchor) {
        iconOptions.iconAnchor = typeof config.iconAnchor === 'function' ? config.iconAnchor(entity) : config.iconAnchor
      }

      const icon = L.divIcon(iconOptions)

      if (existing) {
        const newLatLng = L.latLng(entity.lat, entity.lng)

        // Check if data has changed
        const dataChanged = !existing._entityData ||
          JSON.stringify(existing._entityData) !== JSON.stringify(entity)

        // Update position if moved
        if (!existing.getLatLng().equals(newLatLng)) {
          if (this.map.hasLayer(existing)) {
            this.slideMarker(existing, newLatLng, animationDuration)
          } else {
            existing.setLatLng(newLatLng)
          }
        }

        // Only update icon if data changed
        if (dataChanged) {
          existing.setIcon(icon)

          if (vueInstance) {
            if (existing._vueInstance) {
              existing._vueInstance.$destroy()
            }
            existing._vueInstance = vueInstance
          }

          existing._entityData = entity

          // Refresh tooltip content with the fresh entity
          if (markerTooltip && existing.getTooltip()) {
            const tooltipContent = typeof markerTooltip === 'function'
              ? markerTooltip(entity)
              : markerTooltip
            existing.setTooltipContent(tooltipContent)
          }

          // If the popup is open, re-run its content factory so it shows fresh data
          if (markerPopup && existing.isPopupOpen()) {
            const popup = existing.getPopup()
            if (popup) popup.update()
          }
        }
      } else {
        const marker = L.marker([entity.lat, entity.lng], { icon })
        this.markers[layerType].set(entity.id, marker)
        newMarkers.push(marker)

        marker._entityData = entity

        if (vueInstance) {
          marker._vueInstance = vueInstance
        }

        // Bind tooltip
        if (markerTooltip) {
          const tooltipContent = typeof markerTooltip === 'function'
            ? markerTooltip(entity)
            : markerTooltip
          marker.bindTooltip(tooltipContent)
        }

        // Bind popup — props are re-derived from marker._entityData on every
        // open, so the content reflects the latest incremental (delta) data
        if (markerPopup) {
          const { listeners = {} } = config
          if (typeof markerPopup === 'object' && (markerPopup.render || markerPopup.template || markerPopup._compiled)) {
            // It's a raw Vue component - pass the whole entity as group of props + item and layer
            const getProps = () => ({ ...marker._entityData, item: marker._entityData, layer: marker })
            this.bindVuePopup(marker, markerPopup, getProps, this.store, this.parent, listeners)
          } else if (typeof markerPopup === 'function') {
            const context = { parent: this.parent, layerType, entity }
            const result = markerPopup(entity, this.parent, context)
            // If function returns an object with component property, it's a dynamic component config
            if (result && typeof result === 'object' && result.component) {
              const getProps = () => {
                const freshEntity = marker._entityData
                const fresh = markerPopup(freshEntity, this.parent, { parent: this.parent, layerType, entity: freshEntity })
                const baseProps = fresh?.props || freshEntity
                return { ...baseProps, item: baseProps, layer: marker }
              }
              this.bindVuePopup(marker, result.component, getProps, this.store, this.parent, listeners)
            } else {
              // Otherwise treat as HTML string/element — bind a content function
              // so Leaflet re-evaluates it with fresh data on each open
              marker.bindPopup(() => markerPopup(marker._entityData, this.parent, { parent: this.parent, layerType, entity: marker._entityData }))
            }
          } else if (typeof markerPopup === 'object' && markerPopup.component) {
            // It's a static config { component: X, props: (e) => ({...}) }
            const getProps = () => {
              const freshEntity = marker._entityData
              const context = { parent: this.parent, layerType, entity: freshEntity }
              const baseProps = typeof markerPopup.props === 'function'
                ? markerPopup.props(freshEntity, this.parent, context)
                : (markerPopup.props || freshEntity)
              return { ...baseProps, item: baseProps, layer: marker }
            }
            this.bindVuePopup(marker, markerPopup.component, getProps, this.store, this.parent, listeners)
          } else {
            marker.bindPopup(markerPopup)
          }
        }
      }
    })

    // Add new markers
    if (newMarkers.length > 0) {
      if (typeof this.layers[layerType].addLayers === 'function') {
        this.layers[layerType].addLayers(newMarkers)
      } else {
        newMarkers.forEach(m => this.layers[layerType].addLayer(m))
      }
    }

    // Remove old markers
    if (removeMissing) {
      const markersToRemove = []
      this.markers[layerType].forEach((marker, id) => {
        if (!validIds.has(id)) {
          if (marker._vueInstance) {
            marker._vueInstance.$destroy()
          }
          markersToRemove.push(marker)
          this.markers[layerType].delete(id)
        }
      })

      if (markersToRemove.length > 0) {
        if (typeof this.layers[layerType].removeLayers === 'function') {
          this.layers[layerType].removeLayers(markersToRemove)
        } else {
          markersToRemove.forEach(m => this.layers[layerType].removeLayer(m))
        }
      }
    }

    // Reflect the real total of rendered markers, not just this batch.
    // On incremental fetches `data` is only the delta, so using data.length
    // would clobber the count (e.g. drop it to 1 when a single item arrives).
    this.entityCounts[layerType] = this.markers[layerType].size
  }

  updateShippings(layerKey, shippingsData, config = {}) {
    if (!this.map) return
    // Ensure layer exists and has the correct clustering config
    this.ensureLayer(layerKey, { clustered: config.clustered })
    this.entityCounts[layerKey] = shippingsData.length
    const layer = this.layers[layerKey]

    // Clear and redraw
    layer.clearLayers()

    const { markerPopup } = config

    shippingsData.forEach((ship) => {
      // Use from_lat/from_lng or origin/lat/lng depending on structure
      const fromLat = ship.from_lat || ship.origin?.lat
      const fromLng = ship.from_lng || ship.origin?.lng
      const toLat = ship.to_lat || ship.destination?.lat
      const toLng = ship.to_lng || ship.destination?.lng

      if (fromLat && fromLng && toLat && toLng) {
        const from = [fromLat, fromLng]
        const to = [toLat, toLng]

        // 1. Origin marker (A)
        const fromMarker = L.marker(from, {
          icon: L.divIcon({
            className: 'shipping-marker origin',
            html: '<div class="shipping-pin" style="background: #28a745; color: white; border-radius: 50%; width: 20px; height: 20px; display: flex; align-items: center; justify-content: center; font-size: 10px; border: 1px solid white; box-shadow: 0 0 4px rgba(0,0,0,0.4);">A</div>',
            iconSize: [20, 20],
            iconAnchor: [10, 10]
          })
        }).addTo(layer)

        // 2. Destination marker (B)
        const toMarker = L.marker(to, {
          icon: L.divIcon({
            className: 'shipping-marker destination',
            html: '<div class="shipping-pin" style="background: #dc3545; color: white; border-radius: 50%; width: 20px; height: 20px; display: flex; align-items: center; justify-content: center; font-size: 10px; border: 1px solid white; box-shadow: 0 0 4px rgba(0,0,0,0.4);">B</div>',
            iconSize: [20, 20],
            iconAnchor: [10, 10]
          })
        }).addTo(layer)

        // 3. Line connecting them
        L.polyline([from, to], {
          color: '#fd0',
          dashArray: '2, 3',
          weight: 2,
          opacity: 0.8
        }).addTo(layer)

        // Bind clicks to select the whole shipping entity
        const selectAction = () => {
          this.map.fire('elementSelected', ship)
        }
        fromMarker.on('click', selectAction)
        toMarker.on('click', selectAction)

        // Bind popups if configured
        if (markerPopup) {
          [fromMarker, toMarker].forEach(marker => {
            if (typeof markerPopup === 'function') {
              const context = { parent: this.parent, ship }
              const result = markerPopup(ship, this.parent, context)
              if (result && typeof result === 'object' && result.component) {
                this.bindVuePopup(marker, result.component, result.props || ship, this.store, this.parent)
              } else {
                marker.bindPopup(result)
              }
            } else {
              marker.bindPopup(markerPopup)
            }
          })
        }
      }
    })
  }

  updateTours(layerKey, toursData, config = {}) {
    if (!this.map) return
    this.ensureLayer(layerKey, { type: 'featureGroup' })
    const layer = this.layers[layerKey]
    layer.clearLayers()
    this.entityCounts[layerKey] = toursData.length

    const { markerPopup } = config

    toursData.forEach((tour) => {
      // We expect processed visits if available (from transform), otherwise fallback to stops
      const stops = tour.visits4Show || tour.stops || []
      const latlngs = stops.map((s) => [s.lat, s.lng])
      const color = tour.color || '#3388ff'

      // 1. Draw Polyline
      if (latlngs.length > 1) {
        L.polyline(latlngs, {
          pane: 'toursPane',
          color,
          weight: 2,
          opacity: 0.7,
          lineJoin: 'round'
        }).addTo(layer)
      }

      // 2. Draw Numbered Markers with Arrows
      stops.forEach((stop, index) => {
        if (!stop.lat || !stop.lng) return

        const isPickup = stop.type === 'pickup'
        const arrow = isPickup ? '↑' : '↓'
        const sequence = index + 1

        const numberedIcon = L.divIcon({
          className: 'tour-stop-icon-wrapper',
          html: `
            <div class="tour-stop-pin" style="background: ${stop.visited ? '#28a745' : color}; ${this.textContrastColor(stop.visited ? '#28a745' : color)}">
              <span>${sequence}</span>
              <span style="font-size: 9px; font-weight: 900;">${arrow}</span>
              <!-- span style="position: absolute; top: -12px; right: -8px; font-size: 9px; font-weight: 900; color: ${color}; text-shadow: -1px -1px 0 #fff, 1px -1px 0 #fff, -1px 1px 0 #fff, 1px 1px 0 #fff;">${arrow}</span -->
            </div>
          `,
          iconSize: [26, 26],
          iconAnchor: [13, 13]
        })

        const marker = L.marker([stop.lat, stop.lng], { icon: numberedIcon }).addTo(layer)

        // Bind click to select the tour entity
        marker.on('click', () => {
          this.map.fire('data-selected', { ...tour, cardType: 'tour' })
        })

        // Bind popups
        if (markerPopup) {
          const context = { parent: this.parent, tour, stop, index }
          if (typeof markerPopup === 'function') {
            const result = markerPopup(stop, this.parent, context)
            if (result && typeof result === 'object' && result.component) {
              this.bindVuePopup(marker, result.component, result.props || stop, this.store, this.parent)
            } else {
              marker.bindPopup(result)
            }
          } else {
            marker.bindPopup(markerPopup)
          }
        }
      })
    })
  }

  updateGeofences(layerKey, geoJsonData, config = {}) {
    // Aliasing: support legacy 'geofences' call or new 'geofences_self'
    // If it's the new standard, use it directly. If it's legacy 'geofences', keep it.
    const finalKey = layerKey === 'geofences' ? 'geofences' : layerKey

    this.layerOptions[finalKey] = config
    if (!this.layers[finalKey]) {
      // If for some reason it wasn't initialized in initLayers (for a custom key)
      this.ensureLayer(finalKey, { type: 'geoJSON', ...config })
    }

    const layer = this.layers[finalKey]
    layer.clearLayers()

    if (geoJsonData) {
      const fallbackCount = Array.isArray(geoJsonData) ? geoJsonData.length : 0
      const count = geoJsonData.features ? geoJsonData.features.length : fallbackCount
      this.entityCounts[finalKey] = count
      this.entityCounts[layerKey] = count
      layer.addData(geoJsonData)

      // Post-bind popups/tooltips if configured
      const { markerPopup, listeners = {} } = config
      if (markerPopup) {
        layer.eachLayer((subLayer) => {
          const feature = subLayer.feature
          const data = feature.properties || feature
          // console.log(`[MapService] Binding layer ${L.stamp(subLayer)} with props:`, Object.keys(data))

          // Bind Popup
          if (markerPopup) {
            const isVueComponent = typeof markerPopup === 'object' &&
                (markerPopup.render || markerPopup.template || markerPopup.staticRenderFns || markerPopup._compiled)

            if (isVueComponent) {
              const props = { ...data, item: data, layer: subLayer }
              this.bindVuePopup(subLayer, markerPopup, props, this.store, this.parent, listeners)
            } else if (typeof markerPopup === 'function') {
              const context = { parent: this.parent, layerKey, item: data }
              const result = markerPopup(data, this.parent, context)
              if (result && typeof result === 'object' && result.component) {
                const props = { ...(result.props || data), item: result.props || data, layer: subLayer }
                this.bindVuePopup(subLayer, result.component, props, this.store, this.parent, listeners)
              } else {
                subLayer.bindPopup(result)
              }
            } else if (typeof markerPopup === 'object' && markerPopup.component) {
              const context = { parent: this.parent, layerKey, item: data }
              const baseProps = typeof markerPopup.props === 'function'
                ? markerPopup.props(data, this.parent, context)
                : (markerPopup.props || data)
              const props = { ...baseProps, item: baseProps, layer: subLayer }
              this.bindVuePopup(subLayer, markerPopup.component, props, this.store, this.parent, listeners)
            } else {
              subLayer.bindPopup(markerPopup)
            }
          }
        })
      }
    } else {
      this.entityCounts[finalKey] = 0
      this.entityCounts[layerKey] = 0
    }
  }

  /**
   * Create a popup with a Vue component
   * @param {L.Layer} layer - The layer/marker to bind the popup to
   * @param {VueComponent} ComponentClass - The raw Vue component definition
   * @param {Object} propsData - Props to pass to the component
   * @param {Object} store - Vuex store
   * @param {Vue} parent - Parent Vue instance context
   * @param {Object} listeners - Event listeners { 'event-name': callback }
   */
  // propsData may be a plain object or a function returning one; functions are
  // resolved on every popup open so the content always reflects the latest data
  bindVuePopup(layer, ComponentClass, propsData, store, parent, listeners = {}) {
    const PopupContent = Vue.extend({
      ...ComponentClass,
      store, // Inject store if needed
      parent // Pass the Vue instance as parent
    })

    layer.bindPopup(() => {
      // The factory can re-run while the popup is open (data refresh) — destroy
      // the previous instance first so it doesn't leak
      const previousId = L.stamp(layer)
      const previous = this.popupVueInstances.get(previousId)
      if (previous) {
        previous.$destroy()
        this.popupVueInstances.delete(previousId)
      }

      const mountPoint = document.createElement('div')
      const instance = new PopupContent({
        propsData: typeof propsData === 'function' ? propsData() : propsData
      })

      // Attach event listeners
      if (listeners) {
        Object.keys(listeners).forEach(event => {
          instance.$on(event, listeners[event])
        })
      }

      instance.$mount(mountPoint)

      // Track instance to destroy later
      this.popupVueInstances.set(L.stamp(layer), instance)

      return instance.$el
    })

    layer.on('popupclose', () => {
      const id = L.stamp(layer)
      const instance = this.popupVueInstances.get(id)
      if (instance) {
        instance.$destroy()
        this.popupVueInstances.delete(id)
      }
    })
  }

  /**
   * Report whether the currently rendered layer is a marker cluster group.
   * Returns undefined if the layer hasn't been created yet.
   * @param {string} layerKey
   * @returns {boolean|undefined}
   */
  isLayerClustered(layerKey) {
    const layer = this.layers[layerKey]
    if (!layer) return undefined
    return layer instanceof L.MarkerClusterGroup
  }

  /**
   * Toggle visibility of a layer group
   * @param {string} layerName - Name of the layer ('moovers', 'items', etc)
   * @param {boolean} visible - Visibility state
   */
  toggleLayer(layerName, visible) {
    // Alias handling - simplified
    const finalKey = layerName

    this.layersVisibility[finalKey] = visible

    if (this.layers[finalKey]) {
      if (visible) {
        if (!this.map.hasLayer(this.layers[finalKey])) {
          this.map.addLayer(this.layers[finalKey])
        }
      } else {
        if (this.map.hasLayer(this.layers[finalKey])) {
          this.map.removeLayer(this.layers[finalKey])
        }
        // Do NOT clear data here, as incremental layers loose history.
        // If we want count to be 0, we handle it in getLayerCounts or updateMarkers logic.
      }
    }
  }

  /**
   * Get count of elements in each layer
   * @returns {Object} Counts for each layer
   */
  getLayerCounts() {
    const counts = {
      markers: {},
      groups: {},
      total: {}
    }

    const allKeys = new Set([...Object.keys(this.layers), ...Object.keys(this.entityCounts)])

    allKeys.forEach(layerKey => {
      // If NOT visible, report 0 immediately
      if (!this.layersVisibility[layerKey]) {
        counts.markers[layerKey] = 0
        counts.groups[layerKey] = 0
        counts.total[layerKey] = 0
        return
      }

      let count = this.entityCounts[layerKey] || 0

      if (count === 0) {
        if (this.markers[layerKey] && this.markers[layerKey].size > 0) {
          count = this.markers[layerKey].size
        } else if (this.layers[layerKey] && typeof this.layers[layerKey].getLayers === 'function') {
          count = this.layers[layerKey].getLayers().length
        }
      }

      // Categorize
      const options = this.layerOptions[layerKey] || {}
      if (options.clustered || (this.markers[layerKey] && this.markers[layerKey].size > 0)) {
        counts.markers[layerKey] = count
      } else {
        counts.groups[layerKey] = count
      }

      counts.total[layerKey] = count
    })

    return counts
  }

  /**
   * Create Custom Control
   * @param {VueComponent} Component - Vue Component for the control body
   * @param {Object} propsData - Props to pass
   * @param {string} position - Leaflet position string (topleft, topright, etc.)
   * @param {Object} store - Vuex store
   * @param {Object} parent - Parent Vue instance context
   * @param {Object} listeners - Event listeners { 'event-name': callback }
   */
  addControl(Component, propsData, position = 'topright', store, parent, listeners = {}) {
    const self = this // Closure to access service instance inside L.Control

    const CustomControl = L.Control.extend({
      onAdd: function (map) {
        const div = L.DomUtil.create(
          'div',
          'custom-control-leaflet'
        )
        // Prevent clicks from propagating to map
        L.DomEvent.disableClickPropagation(div)
        L.DomEvent.disableScrollPropagation(div)

        // Enhance component with store and router if available from parent
        const VueControl = Vue.extend({
          ...Component,
          store,
          parent,
          router: parent ? parent.$router : undefined
        })

        const instance = new VueControl({
          propsData
        })

        // Register event listeners
        if (listeners) {
          Object.keys(listeners).forEach(event => {
            instance.$on(event, listeners[event])
          })
        }

        instance.$mount()
        div.appendChild(instance.$el)

        // Track for cleanup
        self.controlVueInstances.push(instance)

        // Store instance on the DOM element for access if needed
        div._vueInstance = instance

        return div
      },
      onRemove: function (map) {
        // Cleanup happens in service destroy or manually if we implemented removeControl
      }
    })

    this.map.addControl(new CustomControl({ position }))
  }

  destroy() {
    if (this.map) {
      // 1. Cancel any pending animations on all markers
      Object.keys(this.markers).forEach(type => {
        this.markers[type].forEach(marker => {
          if (marker._slideReq) {
            cancelAnimationFrame(marker._slideReq)
          }
        })
      })

      // 2. Destroy all active Vue instances in popups
      this.popupVueInstances.forEach((vm) => {
        vm.$destroy()
        if (vm.$el?.parentNode) {
          vm.$el.remove()
        }
      })
      this.popupVueInstances.clear()

      // 3. Destroy all active Vue instances in controls
      if (this.controlVueInstances) {
        this.controlVueInstances.forEach((vm) => {
          vm.$destroy()
          if (vm.$el?.parentNode) {
            vm.$el.remove()
          }
        })
        this.controlVueInstances = []
      }

      // 4. Remove Leaflet map (cleans up internal listeners and DOM)
      this.map.remove()
      this.map = null

      // 5. Clear all element caches and remove layers
      Object.keys(this.markers).forEach(type => {
        this.markers[type].forEach(marker => {
          if (marker._vueInstance) marker._vueInstance.$destroy()
        })
        this.markers[type].clear()
      })

      Object.keys(this.layers).forEach(type => {
        if (this.layers[type] && typeof this.layers[type].clearLayers === 'function') {
          this.layers[type].clearLayers()
        }
      })

      this.markers = {}
      this.layers = {}
      this.layersVisibility = {}
      this.entityCounts = {}
      this.controlVueInstances = []

      console.log('MapService: Fully destroyed and cleaned up.')
    }
  }

  /**
   * Find a Vue instance of a control by its component name or constructor
   * @param {VueComponent|string} Component
   * @returns {Vue|null}
   */
  getControlInstance(Component) {
    const name = typeof Component === 'string' ? Component : Component.name
    return this.controlVueInstances.find((inst) => inst.$options.name === name) || null
  }

  textContrastColor(hexColor) {
    hexColor = hexColor.replace('#', '')
    const r = Number.parseInt(hexColor.slice(0, 2), 16)
    const g = Number.parseInt(hexColor.slice(2, 4), 16)
    const b = Number.parseInt(hexColor.slice(4, 6), 16)
    const yiq = ((r * 299) + (g * 587) + (b * 114)) / 1000
    return (yiq >= 180) ? 'color: #000' : 'color: #fff'
  }

  // --- Drawing / Edition Methods (Leaflet.Editable) ---

  startPolygon(options = {}) {
    if (!this.map?.editTools) return
    return this.map.editTools.startPolygon(null, options)
  }

  startPolyline(options = {}) {
    if (!this.map?.editTools) return
    return this.map.editTools.startPolyline(null, options)
  }

  startMarker(options = {}) {
    if (!this.map?.editTools) return
    return this.map.editTools.startMarker(null, options)
  }

  startRectangle(options = {}) {
    if (!this.map?.editTools) return
    return this.map.editTools.startRectangle(null, options)
  }

  startCircle(options = {}) {
    if (!this.map?.editTools) return
    return this.map.editTools.startCircle(null, options)
  }

  stopDrawing() {
    if (!this.map?.editTools) return
    this.map.editTools.stopDrawing()
  }

  toggleEdit(layer, enable = true) {
    if (!layer || typeof layer.enableEdit !== 'function') return
    if (enable) {
      layer.enableEdit()
    } else {
      layer.disableEdit()
    }
  }
}

export default MapService.getInstance()
export { MapService }
